import React, {useEffect, useState} from 'react';
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import {Alert, AlertTitle, Button, InputAdornment, TextField} from "@mui/material";
import {handleInputChange} from "src/components/form/formHandler";
import {parsePrice} from "src/functions/priceFormatter";
import payoutBatchElementApi from "src/api/payment/payoutBatchElementApi";
import {handleApiError} from "src/api/errorHandler";
import userApi from "src/api/user/userApi";
import ibanChangeApi from "src/api/payment/ibanChangeApi";
import useAuth from "src/hooks/use-auth";

const PayoutForm = ({onSuccess, ibanVerified}) => {
  const initialFormValues = {
    amount: '',
  };
  const [formValues, setFormValues] = useState(initialFormValues)
  const [formErrors, setFormErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [verifyIbanError, setVerifyIbanError] = useState(false);
  const [mostRecentIbanChange, setMostRecentIbanChange] = useState(null);
  const { myUser } = useAuth();

  const fetchIbanChanges = async () => {
    const fetchedIbanChanges = await ibanChangeApi.retrieveCollection();
    const ibanChanges = fetchedIbanChanges['hydra:member'];
    setMostRecentIbanChange(ibanChanges ? ibanChanges[0] : null);
  }

  useEffect(() => {
    fetchIbanChanges();
  }, [])

  const getDataFromForm = () => ({
    amount: parsePrice(formValues.amount),
  })


  const submitForm = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setFormErrors({});

    const data = getDataFromForm();

    try {
      await payoutBatchElementApi.create(data)
      if (onSuccess) {
        onSuccess();
      }
    } catch (e) {
      handleApiError(e, setFormErrors);
      return;
    }

    setFormValues(initialFormValues);
    setSuccess(true);
  }

  const verifyIban = async () => {
    setVerifyIbanError(false);
    try {
      const ibanInitialVerification = await userApi.getIbanInitialVerification(myUser.id);
      window.location.assign(ibanInitialVerification.paymentUrl);
    } catch (e) {
      setVerifyIbanError(true);
      throw e;
    }
  }

  return (
    <form>
      {success && <Alert severity="success" sx={{mb: 2}}>Het bedrag is toegevoegd aan de uitbetalingsbatch.</Alert>}
      {!ibanVerified && (
        <>
          <Alert
            severity="warning" sx={{mb: 2, alignItems: 'center'}}
            variant="outlined"
            action={
              <Button color="secondary" variant="outlined" onClick={verifyIban} sx={{minWidth: '200px'}}>
                Rekeningnummer verifiëren.
              </Button>
            }>
            <AlertTitle>Rekeningnummer niet geverifieerd</AlertTitle>
            Jouw rekeningnummer is nog niet geverifieerd, waardoor je nog geen uitbetaling kan laten doen. Klik op de knop om je IBAN te verifiëren. Je doet hierbij een betaling van 2 cent die bij jouw saldo wordt opgeteld. Zorg ervoor dat je de betaling doet met de bijbehorende rekening.
          </Alert>
          {verifyIbanError && <Alert severity="error" sx={{mb: 2}}>Er is iets misgegaan, probeer het nogmaals.</Alert>}
          {mostRecentIbanChange && mostRecentIbanChange.cancelReason && <Alert severity="error" sx={{mb: 2}}>Jouw laatste verificatie is mislukt: {mostRecentIbanChange.cancelReason}</Alert>}
        </>
      )}
      <Box display="flex" gap={2}>
        <TextField
          name="amount"
          value={formValues.amount}
          onChange={(e) => handleInputChange(e, formValues, setFormValues)}
          label="Bedrag"
          InputProps={{
            startAdornment: <InputAdornment position="start" disableTypography>€</InputAdornment>,
          }}
          placeholder="Hoeveel wilt u uitbetaald worden?"
          required
          error={'amount' in formErrors || 'user' in formErrors}
          helperText={formErrors.amount ? formErrors.amount : formErrors.user}
          disabled={!ibanVerified}
          fullWidth
        />
        <Button variant="outlined" type="submit" onClick={submitForm} sx={{width: "200px"}} disabled={!ibanVerified}>Uitbetalen</Button>
      </Box>
    </form>
  );
}

export default PayoutForm;

PayoutForm.propTypes = {
  onSuccess: PropTypes.func,
};

PayoutForm.defaultProps = {
  onSuccess: null,
};
